import * as React from 'react';
import i18n from 'src/components/lib/i18n/new-i18n';

import useLabelTranslation from 'src/hooks/useLabelTranslation';

import type {
  Duration,
  DurationUnit,
  RecipientType,
  BookingAccount,
  Reminder,
  Rule,
  Capacity,
  EventConfirmationNotifications,
  DefaultConfirmationAndNotifications,
} from 'src/types/scheduler/templates';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';
import type {
  Event,
  EventType,
  DeliveryChannel,
} from 'src/types/scheduler/template-form';
import type {WorkingAssignments} from 'src/types/scheduler/org-templates.js';

import uniqueId from 'lodash/uniqueId';
import {createRawDraftContentState} from 'src/utils/draft';
import {
  Toast,
  ToastTitle,
} from '@spaced-out/ui-design-system/lib/components/Toast';

import {
  HOST,
  CANDIDATE,
  HOST__CANDIDATE,
  ALL_INTERVIEWERS,
  HOST__REQUIRED_INTERVIEWERS,
  EMAIL,
  SMS,
  WHATSAPP,
  REMINDERS,
  CANCEL_EVENT,
  SCHEDULE_EVENT,
  RESCHEDULE_EVENT,
  POST_EVENT_NOTIFICATION,
  REQUEST_RESCHEDULE_EVENT,
  PANEL_TEMPLATE_TYPE,
  ALL_ROUND_ROBIN_TEMPLATE_TYPES,
  SEQUENTIAL_INTERVIEW_TEMPLATE_TYPE,
  CALENDAR_AVAILABILITY,
  WORKING_HOURS,
  CONFLICT_PREVENTION_EVENT_OUTSIDE_SENSE,
  MULTI_EVENT_TEMPLATE_TYPE,
} from 'src/components/scheduler/template-editor/constants';

import {getDurationLabel} from 'src/components/scheduler/utils';


const t = i18n.t;

export const getMeetingOptions = (
  conferenceIntegrations: {
    [key: string]: boolean;
  },
  t: (key: string, value: string) => string,
): {
  iconLeft?: string;
  key: string;
  label?: string;
  disabled: boolean;
}[] => {
  const PROVIDERS = {
    microsoft_teams: {
      iconLeft: 'video',
      key: 'microsoft_teams',
      label: `${t(
        'Microsoft_Teams_video_call',
        'Microsoft Teams (video call)',
      )}`,
      disabled: false,
    },
    google_meet: {
      iconLeft: 'video',
      key: 'google_meet',
      label: `${t('Google_Meet_video_call', 'Google Meet (video call)')}`,
      disabled: false,
    },
    zoom: {
      iconLeft: 'video',
      key: 'zoom',
      label: `${t('Zoom_video_call', 'Zoom (video call)')}`,
      disabled: false,
    },
    webex: {
      iconLeft: 'video',
      key: 'webex',
      label: `${t('Webex_video_call', 'Webex (video call)')}`,
      disabled: false,
    },
  } as const;

  const virtualOptions: Array<{
    disabled: boolean;
    iconLeft: string;
    key: string;
    label: string;
  }> = [];
  const nonVirtualOptions = [
    {
      iconLeft: 'phone',
      key: 'phone_number',
      label: `${t('Phone_Call', 'Phone Call')}`,
      disabled: false,
    },
    {
      iconLeft: 'link',
      key: 'custom_location',
      label: `${t('Custom_Option', 'Custom Option')}`,
      disabled: false,
    },
  ];

  if (conferenceIntegrations.microsoft_teams) {
    virtualOptions.push(PROVIDERS.microsoft_teams);
  }
  if (conferenceIntegrations.google_meet) {
    virtualOptions.push(PROVIDERS.google_meet);
  }
  if (conferenceIntegrations.zoom) {
    virtualOptions.push(PROVIDERS.zoom);
  }
  if (conferenceIntegrations.webex) {
    virtualOptions.push(PROVIDERS.webex);
  }
  return [...virtualOptions, ...nonVirtualOptions];
};

export const getRecipientType = (
  recipientType: string,
  t: (key: string, value: string) => string,
): string => {
  const recipientTypeMap = {
    HOST: `${t('host', 'host')}`,
    CANDIDATE: `${t('attendee', 'attendee')}`,
    HOST__CANDIDATE: `${t('host_attendee', 'host & attendee')}`,
    HOST__REQUIRED_INTERVIEWERS: `${t(
      'host_required_interviewers',
      'host & required interviewers',
    )}`,
    ALL_INTERVIEWERS: `${t('all_interviewers', 'all interviewers')}`,
  } as const;
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly HOST: string; readonly CANDIDATE: string; readonly HOST__CANDIDATE: string; readonly HOST__REQUIRED_INTERVIEWERS: string; readonly ALL_INTERVIEWERS: string; }'.
  return recipientTypeMap[recipientType] || '';
};

export const getDeliveryChannelOptions = (
  t: (key: string, value: string) => string,
  isWhatsappNotificationEnabled: boolean,
): MenuOption[] => {
  return [
    {key: SMS, label: `${t('SMS', 'SMS')}`},
    {key: EMAIL, label: `${t('Email', 'Email')}`},
    ...(isWhatsappNotificationEnabled
      ? [{key: WHATSAPP, label: `${t('Whatsapp', 'Whatsapp')}`}]
      : []),
  ];
};

export const getRequiredQuestions = (
  t: (key: string, value: string) => string,
): string[] => {
  return [
    t('Name', 'Name'),
    t('Email_Address', 'Email Address'),
    t('Phone', 'Phone'),
  ];
};

export const BEFORE_AFTER_OPTIONS = [
  {
    key: 'before',
    label: `${t('Before_events_starts', 'Before events starts')}`,
  },
  {key: 'after', label: `${t('After_events_ends', 'After events ends')}`},
] as const;

export const getTimeBlockOperatorOptions = (
  t: (key: string, value: string) => string,
): MenuOption[] => {
  return [
    {key: 'equals', label: `${t('equals', 'equals')}`},
    {key: 'contains', label: `${t('contains', 'contains')}`},
  ];
};

export const getRoleOptions = (
  t: (key: string, value: string) => string,
): MenuOption[] => {
  return [
    {key: 'required', label: t('Required', 'Required')},
    {key: 'optional', label: t('Optional', 'Optional')},
    {key: 'host', label: t('Host', 'Host')},
  ];
};

export const getQuestionTypeOptions = (
  t: (key: string, value: string) => string,
): MenuOption[] => {
  const QUESTION_TYPE_OPTIONS: MenuOption[] = [
    {label: `${t('Email', 'Email')}`, key: 'email'},
    {label: `${t('Phone', 'Phone')}`, key: 'phone'},
    {
      label: `${t('Multi_Line_Text', 'Multi Line Text')}`,
      key: 'multi_line_text',
    },
    {label: `${t('Number', 'Number')}`, key: 'number'},
    {label: `${t('Dropdown', 'Dropdown')}`, key: 'dropdown'},
    {label: `${t('Checkbox', 'Checkbox')}`, key: 'checkbox'},
    {label: `${t('Consent', 'Consent')}`, key: 'consent'},
  ];
  return QUESTION_TYPE_OPTIONS;
};

export const getDurationInSeconds = (duration: Duration): number => {
  switch (duration.unit) {
    case 'minutes':
      return duration.amount * 60;
    case 'hours':
      return duration.amount * 60 * 60;
    case 'days':
      return duration.amount * 60 * 60 * 24;
    default:
      throw new Error(
        `${t('Invalid_duration_unit', 'Invalid duration unit')} ${
          duration.unit
        }`,
      );
  }
};

export const isValidEmailString = (emailString: string): boolean => {
  const emailArray = emailString.split(',');
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const validEmails =
    emailArray.length > 0 &&
    emailArray.every((email) => {
      const trimmedEmail = email.trim();
      return trimmedEmail.length > 0 && emailRegex.test(trimmedEmail);
    });

  return validEmails;
};

export const getAnswerType = (
  answerType: string,
  t: (key: string, value: string) => string,
): string => {
  if (answerType === 'checkbox') {
    return t('yes_no', 'yes / no');
  } else if (answerType === 'multi_line_text') {
    return t('free_response', 'free response');
  } else {
    return answerType;
  }
};

export const getBookingTypeOptions = (
  t: (key: string, value: string) => string,
): MenuOption[] => {
  return [
    {
      key: CALENDAR_AVAILABILITY,
      label: t(
        'Book_based_on_calendar_availability_within_working_hours',
        'Book based on calendar availability within working hours',
      ),
    },
    {
      key: WORKING_HOURS,
      label: t('Book_based_on_working_hours', 'Book based on working hours'),
    },
  ];
};

export const checkForRoundRobinTemplate = (
  distributionRule: string,
): boolean => {
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"round_robin_availability" | "round_robin_fairness"'.
  return ALL_ROUND_ROBIN_TEMPLATE_TYPES.includes(distributionRule);
};

export const getMenuOptions = ({
  t,
  distributionRule,
  isTeamTemplate,
  isOrgTemplate,
}: {
  t: (key: string, value: string) => string;
  distributionRule: string;
  isTeamTemplate: boolean;
  isOrgTemplate: boolean;
}): MenuOption[] => {
  const isRoundRobinTemplate = checkForRoundRobinTemplate(distributionRule);
  const isPanelTemplate = distributionRule === 'panel_interview';
  const isSequentialInterviewTemplate =
    distributionRule === SEQUENTIAL_INTERVIEW_TEMPLATE_TYPE;

  const options = [
    {key: 'details', label: `${t('Meeting_Details', 'Meeting Details')}`},
    // add 'Scheduling' if not a team template or if it's a sequential interview template
    ...((!isTeamTemplate && !isOrgTemplate) || isSequentialInterviewTemplate
      ? [{key: 'scheduling', label: `${t('Scheduling', 'Scheduling')}`}]
      : []),
    // add 'Sequence Design' for sequential interview template
    ...(isSequentialInterviewTemplate
      ? [
          {
            key: 'sequence-design',
            label: t('Sequence_Design', 'Sequence Design'),
          },
        ]
      : []),
    ...(isRoundRobinTemplate
      ? [
          {
            key: 'add-interviewers-for-round-robin',
            label: `${t('Add_Interviewers', 'Add Interviewers')}`,
          },
        ]
      : []),
    // add 'Add Interviewers' and 'Scheduling'for panel template
    ...(isPanelTemplate
      ? [
          {
            key: 'add-interviewers-for-panel',
            label: t('Add_Interviewers', 'Add Interviewers'),
          },
          {key: 'panel-scheduling', label: t('Scheduling', 'Scheduling')},
        ]
      : []),
    ...(!isSequentialInterviewTemplate
      ? [{key: 'booking-flow', label: t('Booking_Flow', 'Booking Flow')}]
      : []),
    {
      key: 'event-notification',
      label: `${t('Event_Notification', 'Event Notification')} & ${t(
        'Reminders',
        'Reminders',
      )}`,
    },
    {key: 'custom-questions', label: t('Custom_Questions', 'Custom Questions')},
    {
      key: 'other-information',
      label: t('Other_Information', 'Other Information'),
    },
    ...(isOrgTemplate
      ? [
          {
            key: 'template-access',
            label: t('Template_Access', 'Template Access'),
          },
        ]
      : []),
  ];
  return options;
};

export const getEventTypeOptions = (
  t: (key: string, value: string) => string,
): MenuOption[] => {
  const EVENT_TYPE_OPTIONS: MenuOption[] = [
    {key: REMINDERS, label: `${t('Reminder', 'Reminder')}`},
    {key: CANCEL_EVENT, label: `${t('Cancel_Event', 'Cancel Event')}`},
    {
      key: SCHEDULE_EVENT,
      label: `${t('Event_Confirmation', 'Event Confirmation')}`,
    },
    {
      key: RESCHEDULE_EVENT,
      label: `${t('Reschedule_Event', 'Reschedule Event')}`,
    },
    {
      key: REQUEST_RESCHEDULE_EVENT,
      label: `${t('Request_Reschedule_Event', 'Request Reschedule Event')}`,
    },
    {
      key: POST_EVENT_NOTIFICATION,
      label: `${t('Post_Event_Notification', 'Post Event Notification')}`,
    },
  ];

  return EVENT_TYPE_OPTIONS;
};

export const RECIPIENT_VALUES_MAP: {
  [key: string]: string;
} = {
  HOST: 'host',
  CANDIDATE: 'attendee',
  HOST__CANDIDATE: 'hostAttendee',
  HOST__REQUIRED_INTERVIEWERS: 'host', // TODO(Aditya) - for now we can use host's default content
  ALL_INTERVIEWERS: 'allInterviewers',
};

export const getEventBookingOptions = (
  eventType: string,
  t: (key: string, value: string) => string,
): MenuOption[] => {
  const eventTypeToOptionsMap: {
    [key: string]: MenuOption[];
  } = {
    [REMINDERS]: [
      {key: 'before', label: t('Before_event_starts', 'Before event starts')},
    ],
    [CANCEL_EVENT]: [
      {
        key: 'after',
        label: t('After_event_is_cancelled', 'After event is cancelled'),
      },
    ],
    [SCHEDULE_EVENT]: [
      {
        key: 'after',
        label: t('After_event_is_booked', 'After event is booked'),
      },
    ],
    [RESCHEDULE_EVENT]: [
      {
        key: 'after',
        label: t('After_event_is_rescheduled', 'After event is rescheduled'),
      },
    ],
    [REQUEST_RESCHEDULE_EVENT]: [
      {
        key: 'after',
        label: t(
          'After_event_is_requested_to_be_rescheduled',
          'After event is requested to be rescheduled',
        ),
      },
    ],
    [POST_EVENT_NOTIFICATION]: [
      {key: 'after', label: t('After_event_ends', 'After event ends')},
    ],
  };

  return eventTypeToOptionsMap[eventType] || [];
};

export const getDefaultEventContent = ({
  eventType = 'reminders',
  deliveryChannel = 'email',
  recipientType = 'CANDIDATE',
  defaultConfirmationAndNotifications,
}: {
  eventType: string;
  deliveryChannel: string;
  recipientType: RecipientType;
  defaultConfirmationAndNotifications: DefaultConfirmationAndNotifications;
}): Event => {
  const normalizedRecipientType = RECIPIENT_VALUES_MAP[recipientType];
  const defaultEventContentBasedOnEventType =
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'DefaultConfirmationAndNotifications'.
    defaultConfirmationAndNotifications[eventType] || {};

  const getDefaultContent = (content: any) =>
    content || createRawDraftContentState();
  const isReminder = eventType === REMINDERS;
  const isNotificationTypeReminderOrPostEvent =
    checkNotificationTypeReminderOrPostEvent(eventType);
  const isEmail = deliveryChannel === EMAIL;

  const defaultEmailEventContent = {
    id: uniqueId(),
    eventType,
    isBefore: isReminder,
    deliveryChannel: EMAIL,
    timeBefore: isNotificationTypeReminderOrPostEvent
      ? {amount: 5, unit: 'minutes'}
      : {amount: 0, unit: 'minutes'},
    recipientType,
    subject: getDefaultContent(
      defaultEventContentBasedOnEventType[normalizedRecipientType]?.email
        ?.subject,
    ),
    attachments: [],
    body: getDefaultContent(
      defaultEventContentBasedOnEventType[normalizedRecipientType]?.email?.body,
    ),
  } as const;

  const defaultSmsEventContent = {
    id: uniqueId(),
    eventType,
    isBefore: isReminder,
    deliveryChannel: SMS,
    timeBefore: isNotificationTypeReminderOrPostEvent
      ? {amount: 5, unit: 'minutes'}
      : {amount: 0, unit: 'minutes'},
    recipientType,
    note: getDefaultContent(
      defaultEventContentBasedOnEventType[normalizedRecipientType]?.sms?.note,
    ),
  } as const;

  // @ts-expect-error - TS2322 - Type '{ readonly id: string; readonly eventType: string; readonly isBefore: boolean; readonly deliveryChannel: "email"; readonly timeBefore: { amount: number; unit: string; }; readonly recipientType: RecipientType; readonly subject: any; readonly attachments: readonly []; readonly body: any; }' is not assignable to type 'Event'. | TS2322 - Type '{ readonly id: string; readonly eventType: string; readonly isBefore: boolean; readonly deliveryChannel: "sms"; readonly timeBefore: { amount: number; unit: string; }; readonly recipientType: RecipientType; readonly note: any; }' is not assignable to type 'Event'.
  return isEmail ? defaultEmailEventContent : defaultSmsEventContent;
};

const mapAndSortNotifications = (
  eventType: string,
  notifications: Reminder[],
): Event[] => {
  const sortedNotifications = [...notifications].sort(
    (a, b) =>
      getDurationInSeconds(a.timeBefore) - getDurationInSeconds(b.timeBefore),
  );
  const processedNotifications = sortedNotifications.map((event) => ({
    id: uniqueId(),
    eventType,
    ...event,
  }));
  return processedNotifications;
};

export const categorizeAndSortEvents = (
  eventReminders: Reminder[],
  eventConfirmationNotifications: EventConfirmationNotifications,
): {
  allEvents: Event[];
  beforeEventNotifications: Event[];
  postEventNotifications: Event[];
  otherNotifications: Event[];
} => {
  const {
    cancelEvent = [],
    scheduleEvent = [],
    rescheduleEvent = [],
    requestRescheduleEvent = [],
  } = eventConfirmationNotifications;

  const allReminders = eventReminders.map((evt) => ({
    id: uniqueId(),
    eventType:
      evt.isBefore === undefined || evt.isBefore
        ? REMINDERS
        : POST_EVENT_NOTIFICATION,
    // @ts-expect-error - TS2783 - 'isBefore' is specified more than once, so this usage will be overwritten.
    isBefore: evt.isBefore === undefined ? true : evt.isBefore, // if isBefore is undefined, considered it as reminder and make isBefore true
    ...evt,
  }));

  // reminders before event occur
  const remindersBeforeEventOccur = [...allReminders]
    .filter((event) => event.eventType === REMINDERS)
    .sort(
      (a, b) =>
        getDurationInSeconds(b.timeBefore) - getDurationInSeconds(a.timeBefore),
    );

  // reminders after event occur (post event notification)
  const postEventNotifications = [...allReminders]
    .filter((event) => event.eventType === POST_EVENT_NOTIFICATION)
    .sort(
      (a, b) =>
        getDurationInSeconds(a.timeBefore) - getDurationInSeconds(b.timeBefore),
    );

  // confirmation events
  const eventConformationNotifications = scheduleEvent
    ? mapAndSortNotifications('scheduleEvent', scheduleEvent)
    : [];

  // reschedule events
  const eventRescheduleNotifications = rescheduleEvent
    ? mapAndSortNotifications('rescheduleEvent', rescheduleEvent)
    : [];

  // request reschedule events
  const eventRequestRescheduleNotifications = requestRescheduleEvent
    ? mapAndSortNotifications('requestRescheduleEvent', requestRescheduleEvent)
    : [];

  // cancellation events
  const eventCancellationNotifications = cancelEvent
    ? mapAndSortNotifications('cancelEvent', cancelEvent)
    : [];

  // combine all events
  const allEvents = [
    ...remindersBeforeEventOccur,
    ...postEventNotifications,
    ...eventConformationNotifications,
    ...eventRescheduleNotifications,
    ...eventRequestRescheduleNotifications,
    ...eventCancellationNotifications,
  ];

  // confirmations and before reminders events
  const beforeEventNotifications = [
    ...eventConformationNotifications,
    ...remindersBeforeEventOccur,
  ];

  // reschedule events, request reschedule events and cancellation events
  const otherNotifications = [
    ...eventRescheduleNotifications,
    ...eventRequestRescheduleNotifications,
    ...eventCancellationNotifications,
  ];

  return {
    allEvents,
    beforeEventNotifications,
    postEventNotifications,
    otherNotifications,
  };
};

export const getRecipientOptions = ({
  distributionRule,
  eventType,
  deliveryChannel,
  t,
}: {
  distributionRule: string;
  eventType: string;
  deliveryChannel: string;
  t: (key: string, value: string) => string;
}): MenuOption[] => {
  const isPanelTemplate = distributionRule === PANEL_TEMPLATE_TYPE;

  const recipientOptionsMap: {
    [key: string]: MenuOption;
  } = {
    HOST: {label: t('Host', 'Host'), key: 'HOST'},
    CANDIDATE: {label: t('Attendee', 'Attendee'), key: 'CANDIDATE'},
    HOST__CANDIDATE: {
      label: t('Host_Attendee', 'Host & Attendee'),
      key: 'HOST__CANDIDATE',
    },
    HOST__REQUIRED_INTERVIEWERS: {
      label: t('Host_Required_Interviewers', 'Host & Required Interviewers'),
      key: 'HOST__REQUIRED_INTERVIEWERS',
    },
    ALL_INTERVIEWERS: {
      label: t('All_Interviewers', 'All Interviewers'),
      key: 'ALL_INTERVIEWERS',
    },
  };

  const emailRecipients = [
    HOST,
    CANDIDATE,
    HOST__CANDIDATE,
    ...(isPanelTemplate ? [ALL_INTERVIEWERS] : []),
  ];

  const hostAndRequiredInterviewers = isPanelTemplate
    ? [HOST__REQUIRED_INTERVIEWERS]
    : [];

  const recipientKeysMap = {
    scheduleEvent: {
      email: [...emailRecipients],
      sms: [CANDIDATE],
      whatsapp: [CANDIDATE],
    },
    rescheduleEvent: {
      email: [...emailRecipients],
      sms: [CANDIDATE],
      whatsapp: [CANDIDATE],
    },
    cancelEvent: {
      email: [...emailRecipients],
      sms: [CANDIDATE],
      whatsapp: [CANDIDATE],
    },
    reminders: {
      email: [...emailRecipients, ...hostAndRequiredInterviewers],
      sms: [CANDIDATE],
      whatsapp: [CANDIDATE],
    },
    postEventNotification: {
      email: [...emailRecipients, ...hostAndRequiredInterviewers],
      sms: [CANDIDATE],
      whatsapp: [CANDIDATE],
    },
    requestRescheduleEvent: {
      email: [CANDIDATE],
      sms: [CANDIDATE],
      whatsapp: [CANDIDATE],
    },
  } as const;

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly scheduleEvent: { readonly email: readonly string[]; readonly sms: readonly ["CANDIDATE"]; }; readonly rescheduleEvent: { readonly email: readonly string[]; readonly sms: readonly ["CANDIDATE"]; }; readonly cancelEvent: { readonly email: readonly string[]; readonly sms: readonly [...]; }; readonly reminder...'.
  const recipientKeys = recipientKeysMap[eventType]?.[deliveryChannel] || [];

  // @ts-expect-error - TS7006 - Parameter 'recipientKey' implicitly has an 'any' type.
  return recipientKeys.map((recipientKey) => recipientOptionsMap[recipientKey]);
};

export const getEventCardTitle = (
  eventType: EventType,
  deliveryChannel: DeliveryChannel,
  // Add t function as parameter
  t: (key: string, value: string) => string,
): string => {
  const eventCardTitleMap = {
    [REMINDERS]: {
      [EMAIL]: t('Email_Reminder', 'Email Reminder'),
      [SMS]: t('SMS_Reminder', 'SMS Reminder'),
      [WHATSAPP]: t('Whatsapp_Reminder', 'Whatsapp Reminder'),
    },
    [SCHEDULE_EVENT]: {
      [EMAIL]: t('Email_Confirmation', 'Email Confirmation'),
      [SMS]: t('SMS_Confirmation', 'SMS Confirmation'),
      [WHATSAPP]: t('Whatsapp_Confirmation', 'Whatsapp Confirmation'),
    },
    [CANCEL_EVENT]: {
      [EMAIL]: t('Email_Cancel_Notification', 'Email Cancel Notification'),
      [SMS]: t('SMS_Cancel_Notification', 'SMS Cancel Notification'),
      [WHATSAPP]: t(
        'Whatsapp_Cancel_Notification',
        'Whatsapp Cancel Notification',
      ),
    },
    [RESCHEDULE_EVENT]: {
      [EMAIL]: t(
        'Email_Reschedule_Notification',
        'Email Reschedule Notification',
      ),
      [SMS]: t('SMS_Reschedule_Notification', 'SMS Reschedule Notification'),
      [WHATSAPP]: t(
        'Whatsapp_Reschedule_Notification',
        'Whatsapp Reschedule Notification',
      ),
    },
    [REQUEST_RESCHEDULE_EVENT]: {
      [EMAIL]: t(
        'Email_Request_to_Reschedule_Notification',
        'Email Request to Reschedule Notification',
      ),
      [SMS]: t(
        'SMS_Request_to_Reschedule_Notification',
        'SMS Request to Reschedule Notification',
      ),
      [WHATSAPP]: t(
        'Whatsapp_Request_to_Reschedule_Notification',
        'Whatsapp Request to Reschedule Notification',
      ),
    },
    [POST_EVENT_NOTIFICATION]: {
      [EMAIL]: t(
        'Email_Post_Event_Notification',
        'Email Post Event Notification',
      ),
      [SMS]: t('SMS_Post_Event_Notification', 'SMS Post Event Notification'),
      [WHATSAPP]: t(
        'Whatsapp_Post_Event_Notification',
        'Whatsapp Post Event Notification',
      ),
    },
  } as const;

  return (
    eventCardTitleMap[eventType][deliveryChannel] ||
    t('Event_Notification', 'Event Notification')
  );
};

export const getTimeMessage = (
  isBefore: boolean,
  timeBefore: Duration,
  eventType: string,
  t: (key: string, value: string) => string,
): string => {
  const timeMessage = getDurationLabel(timeBefore, t);

  const eventMessages: {
    [key: string]: {
      zeroTime: string;
      withTime: (time: string) => string;
    };
  } = {
    scheduleEvent: {
      zeroTime: t(
        'sent_upon_booking_confirmation',
        'sent upon booking confirmation',
      ),
      withTime: (time) => `${time} after the event is booked`,
    },
    rescheduleEvent: {
      zeroTime: t('when_event_is_rescheduled', 'when event is rescheduled'),
      withTime: (time) =>
        `${time} ${t(
          'after_the_event_is_rescheduled',
          'after the event is rescheduled',
        )}`,
    },
    requestRescheduleEvent: {
      zeroTime: t(
        'when_event_is_requested_to_be_rescheduled',
        'when event is requested to be rescheduled',
      ),
      withTime: (time) =>
        `${time} ${t(
          'after_the_event_is_requested_to_be_rescheduled',
          'after the event is requested to be rescheduled',
        )}`,
    },
    cancelEvent: {
      zeroTime: t('when_event_is_cancelled', 'when event is cancelled'),
      withTime: (time) =>
        `${time} ${t(
          'after_the_event_is_cancelled',
          'after the event is cancelled',
        )}`,
    },
    reminders: {
      zeroTime: '',
      withTime: (time) =>
        `${time} ${
          isBefore === undefined || isBefore
            ? t('before', 'before')
            : t('after', 'after')
        } ${t('the_event', 'the event')}`,
    },
    postEventNotification: {
      zeroTime: '',
      withTime: (time) =>
        `${time} ${
          isBefore === undefined || isBefore
            ? t('before', 'before')
            : t('after', 'after')
        } ${t('the_event', 'the event')}`,
    },
  };

  const eventConfig = eventMessages[eventType];
  if (!eventConfig) {
    return 'Event Notification';
  }

  return timeBefore.amount === 0
    ? eventConfig.zeroTime
    : eventConfig.withTime(timeMessage);
};

export const getUnitsAvailable = (eventType: string): DurationUnit[] => {
  const isDaysUnitsAllowed =
    checkNotificationTypeReminderOrPostEvent(eventType);
  return isDaysUnitsAllowed
    ? ['minutes', 'hours', 'days']
    : ['minutes', 'hours'];
};

export const checkNotificationTypeReminderOrPostEvent = (
  eventType: string,
): boolean => {
  return [POST_EVENT_NOTIFICATION, REMINDERS].includes(eventType);
};

export const normalizeEventsByType = (
  allEvents: Event[],
): {
  eventReminders: Reminder[];
  eventConfirmationNotifications: EventConfirmationNotifications;
} => {
  const eventReminders: Array<Reminder> = [];
  const eventConfirmationNotifications: Record<string, any> = {};

  allEvents.forEach((evt) => {
    const {id, eventType, ...restEvent} = evt;
    if (checkNotificationTypeReminderOrPostEvent(eventType)) {
      // @ts-expect-error - TS2345 - Argument of type '{ isBefore: boolean; timeBefore: Duration; body?: any; recipientType: RecipientType; deliveryChannel: "email" | "sms"; note?: any; subject?: any; attachments?: { filePath: string; fileName: string; fileContainer: string; }[] | undefined; }' is not assignable to parameter of type 'Reminder'.
      eventReminders.push(restEvent);
    } else {
      if (!eventConfirmationNotifications[eventType]) {
        eventConfirmationNotifications[eventType] = [restEvent];
      } else {
        eventConfirmationNotifications[eventType].push(restEvent);
      }
    }
  });

  return {eventReminders, eventConfirmationNotifications};
};

const normalizeMaxEventSize = (value: number | null | undefined): number =>
  value === 0 || value == null ? 1 : value;

export const normalizeBookingAccounts = (
  bookingAccounts: BookingAccount[],
  distributionRule?: string,
): BookingAccount[] => {
  const isMultiEvent = distributionRule === MULTI_EVENT_TEMPLATE_TYPE;

  return bookingAccounts.map((bookingAccount) => {
    const slotType = bookingAccount.bookingSlotType;
    let normalizedAccount: BookingAccount;

    if (slotType === 'date_based_slot') {
      normalizedAccount = {
        ...bookingAccount,
        openBookingTimeBlocks: null,
      };
    } else if (slotType === 'timeblock_based_slot') {
      normalizedAccount = {
        ...bookingAccount,
        openBookingDates: null,
      };
    } else if (slotType === 'week_based_slot') {
      normalizedAccount = {
        ...bookingAccount,
        openBookingDates: null,
        openBookingTimeBlocks: null,
      };
    } else {
      normalizedAccount = {...bookingAccount};
    }

    // for multi-event template, normalized the max event size
    if (isMultiEvent) {
      if (normalizedAccount.openBookingSlots?.length) {
        normalizedAccount = {
          ...normalizedAccount,
          openBookingSlots: normalizedAccount.openBookingSlots.map((slot) => ({
            ...slot,
            maxEventSize: normalizeMaxEventSize(slot.maxEventSize),
          })),
        };
      }
      if (normalizedAccount.openBookingDates?.length) {
        normalizedAccount = {
          ...normalizedAccount,
          openBookingDates: normalizedAccount.openBookingDates.map((date) => ({
            ...date,
            maxEventSize: normalizeMaxEventSize(date.maxEventSize),
          })),
        };
      }
      if (normalizedAccount.openBookingTimeBlocks?.length) {
        normalizedAccount = {
          ...normalizedAccount,
          openBookingTimeBlocks: normalizedAccount.openBookingTimeBlocks.map(
            (block) => ({
              ...block,
              maxEventSize: normalizeMaxEventSize(block.maxEventSize),
            }),
          ),
        };
      }
    }

    return normalizedAccount;
  });
};

export const normalizeCapacity = (
  capacity: Capacity[],
  distributionRule?: string,
): Capacity[] => {
  if (distributionRule === MULTI_EVENT_TEMPLATE_TYPE) {
    return capacity.map((c) => ({
      ...c,
      capacity: c.capacity ?? 1,
    }));
  }
  return capacity;
};

export const validateBookingAccounts = (
  bookingAccounts: BookingAccount[],
): boolean => {
  return bookingAccounts
    .filter((account) => account.bookingSlotType === 'timeblock_based_slot')
    .some((account) =>
      (account.openBookingTimeBlocks || []).some((block) => block.title === ''),
    );
};

export const validateConflictPreventionRules = (
  conflictPreventionRules: Rule[],
): boolean => {
  return conflictPreventionRules.some(
    (rule) =>
      rule.value === '' &&
      rule.ruleType !== CONFLICT_PREVENTION_EVENT_OUTSIDE_SENSE,
  );
};

export const validateNoAvailabilityRules = (
  noAvailabilityRules: Rule[],
): boolean => {
  return noAvailabilityRules[0]?.value === '';
};

export const ErrorToast = () => {
  const {t} = useLabelTranslation();

  return (
    <Toast semantic="danger">
      <ToastTitle>{`${t(
        'Something_went_wrong',
        'Something went wrong',
      )}`}</ToastTitle>
    </Toast>
  );
};

export const getWhatsappTemplateFilterOptions = (
  t: (key: string, value: string) => string,
): MenuOption[] => {
  return [
    {key: 'all', label: t('All', 'All')},
    {key: 'system-default', label: t('System_Default', 'System Default')},
    {key: 'other', label: t('Other', 'Other')},
  ];
};

export const getAccessSummaryMessage = (
  t: (key: string, fallback: string) => string,
  workingAssignments: WorkingAssignments,
): string => {
  const {inclusions, exclusions, isAssignedToAll} = workingAssignments;

  if (isAssignedToAll) {
    return t(
      'You\'re_about_to_grant_access_to_the_entire_organization',
      'You\'re about to grant access to the entire organization.',
    );
  }

  // Calculate all 4 combinations
  const securityGroupsGranted = inclusions.securityGroupIds.length;
  const securityGroupsRemoved = exclusions.securityGroupIds.length;
  const individualsGranted = inclusions.agentIds.length;
  const individualsRemoved = exclusions.agentIds.length;

  // Build granted and removed parts separately
  const grantedParts = [];
  const removedParts = [];

  // Add granted parts
  if (securityGroupsGranted > 0) {
    const label =
      securityGroupsGranted === 1
        ? t('security_group', 'security group')
        : t('security_groups', 'security groups');
    grantedParts.push(`${securityGroupsGranted} ${label}`);
  }

  if (individualsGranted > 0) {
    const label =
      individualsGranted === 1
        ? t('individual', 'individual')
        : t('individuals', 'individuals');
    grantedParts.push(`${individualsGranted} ${label}`);
  }

  // Add removed parts
  if (securityGroupsRemoved > 0) {
    const label =
      securityGroupsRemoved === 1
        ? t('security_group', 'security group')
        : t('security_groups', 'security groups');
    removedParts.push(`${securityGroupsRemoved} ${label}`);
  }

  if (individualsRemoved > 0) {
    const label =
      individualsRemoved === 1
        ? t('individual', 'individual')
        : t('individuals', 'individuals');
    removedParts.push(`${individualsRemoved} ${label}`);
  }

  // Build final message
  const messageParts = [];

  if (grantedParts.length > 0) {
    messageParts.push(
      t('grant_access_to', 'grant access to') +
        ' ' +
        grantedParts.join(` ${t('and', 'and')} `),
    );
  }

  if (removedParts.length > 0) {
    messageParts.push(
      t('remove_access_from', 'remove access from') +
        ' ' +
        removedParts.join(` ${t('and', 'and')} `),
    );
  }

  if (messageParts.length === 0) {
    return t('No_access_changes_to_confirm', 'No access changes to confirm.');
  }

  return (
    t('You\'re_about_to', 'You\'re about to') +
    ' ' +
    messageParts.join(` ${t('and', 'and')} `) +
    '.'
  );
};

export const getTimelineIcon = (deliveryChannel: string): string => {
  switch (deliveryChannel) {
    case SMS:
      return 'sms';
    case WHATSAPP:
      return 'whatsapp';
    default:
      return 'envelope';
  }
};

export const replaceRulesByType = ({
  existingRules,
  targetRuleTypes,
  updatedRulesForTargetedRuleTypes,
}: {
  existingRules: Rule[];
  targetRuleTypes: readonly string[];
  updatedRulesForTargetedRuleTypes: Rule[];
}): Rule[] => {
  return [
    ...existingRules.filter((r) => !targetRuleTypes.includes(r.ruleType)),
    ...updatedRulesForTargetedRuleTypes,
  ];
};

export const isInvalidCapacity = (
  capacity: number | string | null | undefined,
): boolean => {
  const isInvalidCapacity =
    capacity === '' ||
    capacity === null ||
    capacity === undefined ||
    Number(capacity) < 0 ||
    Number(capacity) > 100;

  return isInvalidCapacity;
};

export const hasInvalidCapacity = (capacity: Capacity[]): boolean => {
  return capacity.some((c) => isInvalidCapacity(c.capacity ?? ''));
};
